<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>

    <v-card  v-if="event">
      <v-toolbar elevation="0" color="grey lighten-5">
        <v-toolbar-title class="ml-3 py-2 subtitle">
          Participants
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <div class="actions">
          
        </div>
      </v-toolbar>
      <v-divider/>

      <div >
        <v-alert tile v-if="!event.published" type="info">This event is not yet published. An event must be published before it can be used or viewed.</v-alert>
        <v-card-text>      
          <p v-if="tenant.isDefault">
            Please read our <a href="https://sodisp.freshdesk.com/support/solutions/folders/77000137491" target="_blank">Frequently asked questions</a> on creating and managing events for tips and tricks.
          </p>
          <p>
            Click on a row to open the details screen of each participant for additional information and options.
          </p>

          <div v-if="event.verification_method === 'ALLOWLIST'">
            <h3>Invite participants</h3>
            <p>
              Send this link to participants to join this event. Anyone with this link may join.
              <span v-if="eventUtil.isBasicPlan()"><router-link :to="{name: 'eventmanagerLicense', params: {id: event.id}}">Upgrade to a PRO plan</router-link> to import Excel files with your participants.</span>
            </p>
            <code v-if="tenant.id != 'cofi'">{{joinUrl}}</code> 
            <v-btn text small @click="$helpers.copyToClipboard(joinUrl, $toast)"><v-icon small color="accent" class="mr-2">fa-copy</v-icon> Copy</v-btn>
            <v-dialog v-if="tenant.id == 'cofi'" max-width="400px">
              <template v-slot:activator="{ on }">
                <v-btn text small color="accent" v-on="on">
                  <v-icon small class="mr-2">fadl fa fa-qrcode</v-icon> View QR code
                </v-btn>
              </template>
              <v-card>
                <v-card-title>Join Event QR</v-card-title>
                <v-card-text>Scan this QR code with your phone to quickly join this event using our app. This will download the iOS or Android depending on the phone.</v-card-text>
                <v-card-text>Share this code with the other participants so they can join as well.</v-card-text>
                <img :src="`https://chart.googleapis.com/chart?cht=qr&chs=400x400&chl=${encodeURIComponent(joinUrl)}`" width="400" height="400"/>
              </v-card>
            </v-dialog>
            <v-btn text small color="accent" @click="emailInviteToMe">
              <v-icon small class="mr-2">fadl fa fa-envelope</v-icon> Email me
            </v-btn>
          </div>
          <div v-else-if="event.verification_method === 'REG_DATA'">
            <h3>Invite participants</h3>
            <p>Manage or Upload <router-link :to="{name: 'eventmanagerRegistrations', params: {id: event.id}}">Registration Data</router-link> to send out personal invite links</p>
          </div>
        </v-card-text>

        <v-card-title class="subtitle pt-4">
          {{ !participants ? 0 : participants.length}} joined users
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="fa-search"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-card-title>
        <v-data-table v-if="participants"
          :headers="headers"
          :items="participants"
          :items-per-page="15"
          :search="search"
          @click:row="item => showProfileDetails(item.profile_id)"
          >
          <template v-slot:item.join_date="{ item }">
            {{ item.join_date | localDate('L LT') }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon small color="primary" class="mr-2" title="Profile details" @click.stop="showProfileDetails(item.profile_id)">fa-info-circle</v-icon>
          </template>

        </v-data-table>
      </div>      
      <ProfileDetailsDialog :eventId="event.id" :event="event" ref="profileDetailsDialog"/>
    </v-card>
  </div>      
</template>

<script>
import { mapGetters } from "vuex";
import eventManagerService from "@/services/eventManagerService";
import { EventBus } from '@/plugins/eventbus.js';
import ProfileDetailsDialog from "@/components/ProfileDetailsDialog.vue";
import Header from './_Header.vue'
import EventUtil from "@/util/eventUtil";
import tenants from '@/data/tenants.config'
const tenant = tenants.current();

export default {
  name: "Participants",
  components: {
    ProfileDetailsDialog,
    Header,
  },
  props: {
  },
  data() {
    return {
      tenant: tenant,
      event: null,
      participants: null,
      search: null,
      licenseInfo: null,
      joinInfo: null,
      headers: [
        { text: 'Actions', align: 'start', sortable: false, filterable: false, value: 'actions',},
        { text: 'Joined at', align: 'start', sortable: true, filterable: false, value: 'join_date',},
        { text: 'Profile Name', align: 'start', sortable: true, value: 'name',},
        { text: 'Profile Id', align: 'start', sortable: true, value: 'profile_id_last_digits',},
        { text: 'Code', align: 'start', sortable: true, value: 'verification_code',},
        { text: 'Bib', align: 'start', sortable: true, value: 'reg.b',},
        { text: 'Full name', align: 'start', sortable: true, value: 'reg.n',},
        { text: 'First name', align: 'start', sortable: true, value: 'reg.gn',},
        { text: 'Last name', align: 'start', sortable: true, value: 'reg.fn',},
        { text: 'Team', align: 'start', sortable: true, value: 'reg.g',},
        { text: 'Leaderboard', align: 'start', sortable: true, value: 'reg.r',},
        { text: 'Gender', align: 'start', sortable: true, value: 'reg.ge',},
        { text: 'Category', align: 'start', sortable: true, value: 'reg.ca',},
        { text: 'Email', align: 'start', sortable: true, value: 'reg.e',},
        { text: 'Group', align: 'start', sortable: true, value: 'reg.g',},
      ],
    };
  },
  async mounted() {
    await this.getProfile();
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
  },
  methods: {
    async getEvent(id) {
      var response = await eventManagerService.get(id);
      this.event = response.data;
      this.participants = (await eventManagerService.getParticipants(id)).data.data;
      this.groupParticipants = (await eventManagerService.getGroupParticipants(id)).data.data;
      this.licenseInfo = (await eventManagerService.getLicenseStatus(id)).data;

      this.joinInfo = { join_url: this.event.join_url };
      if (this.event.verification_method === 'ALLOWLIST') {
        this.joinInfo = (await eventManagerService.getJoinInfo(id)).data;
      }
    },

    async getProfile() {
      if (this.user) {
        await this.getEvent(this.$route.params.id);
      }
    },

    async emailInviteToMe() {
      const response = await eventManagerService.sendInviteEventEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The invite email is on it\'s way! Please check your inbox in a few minutes from now.');
    },

    async showProfileDetails(id) {
      this.$refs.profileDetailsDialog.open(id);
    }

  },
  computed: {
    breadcrumbItems() {
      return !this.event ? [] : [
        { text: 'Event Manager', exact: true, to: { name: 'eventmanager' } },
        { text: this.event.name, exact: true, to: { name: 'eventmanagerView', params: {id: this.event.id}} },
        { text: 'Joined Users', disabled: true },
      ];
    },
    joinUrl() {
      if (this.event == null) {
        return null;
      }
      return this.joinInfo ? this.joinInfo.join_url : this.event.join_url;
    },
    eventUtil() {
      return new EventUtil(this, this.event, this.race);
    },
    ...mapGetters({
      user: "user"
    })
  },
};
</script>
<style lang="scss">
</style>

